import React, { useCallback, useState } from 'react';

import { useSnackbar } from 'notistack';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';

import { Add, ArrowBack, CopyAll, Upload } from '@mui/icons-material';
import { Box, Button, Card, CardContent, MenuItem, Modal, TextField, Typography } from '@mui/material';
import Grid from '@mui/material/Grid';

import useCurrentUser from '~/hooks/useCurrentUser';

import {
  MakeMeAnOfferErrorMeta,
  MakeMeAnOfferProdUpsertMeta,
  MakeMeAnOfferRegularMeta,
  ProgressStatusResponse,
  getProgressStatuses,
  makeMeAnOffer,
} from '~/services/AccommodationService';

import asyncPoll from '~/utils/asyncPoll';

import MakeMeAnOfferCreateStatus, { MakeMeAnOfferCreateStatusProps } from './MakeMeAnOfferCreateStatus';

// Type guard functions
function isProdUpsertMeta(meta: ProgressStatusResponse['meta']): meta is MakeMeAnOfferProdUpsertMeta {
  return 'validateOffer' in meta;
}

function isRegularMeta(meta: ProgressStatusResponse['meta']): meta is MakeMeAnOfferRegularMeta {
  return 'createOffer' in meta;
}

function isErrorMeta(meta: ProgressStatusResponse['meta']): meta is MakeMeAnOfferErrorMeta {
  return 'error' in meta;
}

const MODAL_STYLE = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: '90%',
  maxHeight: '90vh',
  bgcolor: 'background.paper',
  boxShadow: 24,
  borderRadius: 2,
  p: 3,
  overflowY: 'auto',
};

type OfferType = 'hotel' | 'tactical_ao_hotel';
type OfferEnvironmentType = 'production' | 'staging';

interface MakeMeAnOffer {
  type?: 'prod-upsert';
  offerId?: string;
  offerEnvironment?: OfferEnvironmentType;
  offerType?: OfferType;
  name?: string;
  city?: string;
  country?: string;
  vendorId?: string;
  vendorEmail?: string;
}

enum MAKE_ME_AN_OFFER_SETUP_STEPS {
  CHOOSE_TYPE,
  FILL_FORM,
  PROCESSING,
}

interface Props {
  open: boolean;
  onClose: () => void;
}

export default function MakeMeAnOfferModal({ open, onClose }: Props) {
  const { user } = useCurrentUser();

  const [currentStep, setCurrentStep] = useState<MAKE_ME_AN_OFFER_SETUP_STEPS>(
    MAKE_ME_AN_OFFER_SETUP_STEPS.CHOOSE_TYPE,
  );
  const [selectedType, setSelectedType] = useState<'copy' | 'new' | 'import' | null>(null);
  const [loading, setLoading] = useState(false);

  const { enqueueSnackbar } = useSnackbar();

  const makeMeAnOfferInitialValues: MakeMeAnOffer = {
    offerId: '',
    offerType: 'hotel',
    name: `${user?.fullName} test offer - DO NOT USE`,
    city: 'Bali',
    country: 'Indonesia',
    vendorId: '00128000009UXNYAA4',
    vendorEmail: user?.email,
  };

  const [makeMeAnOfferStatus, setMakeMeAnOfferStatus] = useState<MakeMeAnOfferCreateStatusProps['status']>({});

  const [offerUrl, setOfferUrl] = useState<string | null>(null);
  const [createdOfferName, setCreatedOfferName] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const { control, handleSubmit } = useForm<MakeMeAnOffer>({
    mode: 'onBlur',
    defaultValues: makeMeAnOfferInitialValues,
  });

  const onModalClose = useCallback(() => {
    setOfferUrl(null);
    setCreatedOfferName(null);
    setMakeMeAnOfferStatus({});
    setErrorMessage(null);
    setSelectedType(null);
    setCurrentStep(MAKE_ME_AN_OFFER_SETUP_STEPS.CHOOSE_TYPE);
    onClose();
  }, [onClose]);

  const handleSubmission = useCallback<SubmitHandler<MakeMeAnOffer>>(
    async (data) => {
      setLoading(true);
      setErrorMessage(null);
      setCurrentStep(MAKE_ME_AN_OFFER_SETUP_STEPS.PROCESSING);

      const payload =
        selectedType === 'import'
          ? {
              type: 'prod-upsert' as const,
              offerId: data.offerId,
            }
          : selectedType === 'copy'
          ? {
              offerId: data.offerId,
              offerEnvironment: data.offerEnvironment,
              vendorEmail: data.vendorEmail,
            }
          : {
              offerType: data.offerType,
              name: data.name,
              city: data.city,
              country: data.country,
              vendorId: data.vendorId,
              vendorEmail: data.vendorEmail,
            };

      try {
        const { result } = await makeMeAnOffer(payload);
        const jobId = result.id;

        try {
          await asyncPoll({
            apiCall: () => getProgressStatuses(jobId),
            validateFunction: async ({ result }) => {
              const makeTestOffer = result.find((status) => status.type === 'makeTestOffer');
              if (!makeTestOffer) {
                return false;
              }

              const meta = makeTestOffer.meta;

              // Check if this is a prod upsert flow
              if (isProdUpsertMeta(meta)) {
                // Prod upsert flow
                setMakeMeAnOfferStatus({
                  validateOffer: meta.validateOffer ?? 'awaiting',
                  validateProperty: meta.validateProperty ?? 'awaiting',
                  validateVendor: meta.validateVendor ?? 'awaiting',
                  upsertVendor: meta.upsertVendor ?? 'awaiting',
                  upsertProperty: meta.upsertProperty ?? 'awaiting',
                  upsertOffer: meta.upsertOffer ?? 'awaiting',
                  resyncOffer: meta.resyncOffer ?? 'awaiting',
                });
              } else if (isRegularMeta(meta)) {
                // Regular flow
                setMakeMeAnOfferStatus({
                  createOffer: meta.createOffer ?? 'awaiting',
                  createProperty: meta.createProperty ?? 'awaiting',
                  createPackages: meta.createPackages ?? 'awaiting',
                  createSchedules: meta.createSchedules ?? 'awaiting',
                  createAvailability: meta.createAvailability ?? 'awaiting',
                });
              }

              if (makeTestOffer.state === 'completed') {
                setLoading(false);
                if (isProdUpsertMeta(meta) || isRegularMeta(meta)) {
                  setOfferUrl(`/offers/${meta.offerId}`);
                  setCreatedOfferName(meta.offerName);
                }
                return true;
              } else if (makeTestOffer.state === 'failed') {
                const errorMsg = makeTestOffer.message || 'Failed to create offer';
                const metaError = isErrorMeta(makeTestOffer.meta) ? makeTestOffer.meta.error : undefined;
                const displayError = metaError || errorMsg;

                setErrorMessage(displayError);
                enqueueSnackbar(displayError, { variant: 'error' });
                setLoading(false);
                return true;
              } else {
                return false;
              }
            },
            maxTime: 300000, // 5 minutes
          });
        } catch (e) {
          const errorMsg = e instanceof Error ? e.message : 'An unexpected error occurred';
          setErrorMessage(errorMsg);
          enqueueSnackbar(errorMsg, { variant: 'error' });
          setLoading(false);
        }
      } catch (error) {
        const errorMsg = error instanceof Error ? error.message : 'Failed to submit offer creation request';
        setErrorMessage(errorMsg);
        enqueueSnackbar(errorMsg, { variant: 'error' });
        setLoading(false);
      }
    },
    [selectedType, enqueueSnackbar],
  );

  const handleCancel = useCallback(() => {
    if (currentStep === MAKE_ME_AN_OFFER_SETUP_STEPS.PROCESSING && loading) {
      // Don't allow cancel during processing
      return;
    }
    setSelectedType(null);
    setCurrentStep(MAKE_ME_AN_OFFER_SETUP_STEPS.CHOOSE_TYPE);
  }, [currentStep, loading]);

  return (
    <Modal open={open} onClose={onModalClose}>
      <Box sx={MODAL_STYLE}>
        <form onSubmit={handleSubmit(handleSubmission)}>
          <Typography variant="h4" sx={{ mb: 3, fontWeight: 600 }}>
            Make me an offer
          </Typography>

          {currentStep === MAKE_ME_AN_OFFER_SETUP_STEPS.CHOOSE_TYPE && (
            <Box sx={{ py: 4 }}>
              <Typography variant="h6" sx={{ mb: 3, textAlign: 'center' }}>
                Choose how you want to create an offer
              </Typography>
              <Grid container spacing={3}>
                <Grid size={{ xs: 12, md: 4 }}>
                  <Card
                    sx={{
                      p: 3,
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                      '&:hover': {
                        boxShadow: 6,
                        transform: 'translateY(-4px)',
                      },
                    }}
                    onClick={() => {
                      setSelectedType('copy');
                      setCurrentStep(MAKE_ME_AN_OFFER_SETUP_STEPS.FILL_FORM);
                    }}
                  >
                    <CardContent>
                      <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                        <CopyAll sx={{ fontSize: 48, color: 'primary.main' }} />
                      </Box>
                      <Typography variant="h6" sx={{ mb: 1, textAlign: 'center' }}>
                        Copy Offer
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center' }}>
                        Copy an existing offer from production or staging with all its details
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>

                <Grid size={{ xs: 12, md: 4 }}>
                  <Card
                    sx={{
                      p: 3,
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                      '&:hover': {
                        boxShadow: 6,
                        transform: 'translateY(-4px)',
                      },
                    }}
                    onClick={() => {
                      setSelectedType('new');
                      setCurrentStep(MAKE_ME_AN_OFFER_SETUP_STEPS.FILL_FORM);
                    }}
                  >
                    <CardContent>
                      <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                        <Add sx={{ fontSize: 48, color: 'primary.main' }} />
                      </Box>
                      <Typography variant="h6" sx={{ mb: 1, textAlign: 'center' }}>
                        New Offer
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center' }}>
                        Create a brand new test offer with mock data
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>

                <Grid size={{ xs: 12, md: 4 }}>
                  <Card
                    sx={{
                      p: 3,
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                      '&:hover': {
                        boxShadow: 6,
                        transform: 'translateY(-4px)',
                      },
                    }}
                    onClick={() => {
                      setSelectedType('import');
                      setCurrentStep(MAKE_ME_AN_OFFER_SETUP_STEPS.FILL_FORM);
                    }}
                  >
                    <CardContent>
                      <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                        <Upload sx={{ fontSize: 48, color: 'primary.main' }} />
                      </Box>
                      <Typography variant="h6" sx={{ mb: 1, textAlign: 'center' }}>
                        Import from Prod
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center' }}>
                        Import a production offer directly into staging
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            </Box>
          )}

          {currentStep === MAKE_ME_AN_OFFER_SETUP_STEPS.FILL_FORM && (
            <Box>
              <Button
                startIcon={<ArrowBack />}
                onClick={() => {
                  setSelectedType(null);
                  setCurrentStep(MAKE_ME_AN_OFFER_SETUP_STEPS.CHOOSE_TYPE);
                }}
                sx={{ mb: 2 }}
              >
                Back
              </Button>

              {selectedType === 'copy' && (
                <Box>
                  <Typography variant="h6" sx={{ mb: 2 }}>
                    Copy Offer Details
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid size={{ xs: 12 }} sx={{ mb: 1 }}>
                      <Typography variant="body2">Provide the offer ID of the offer you want to copy.</Typography>
                      <Typography variant="body2">
                        Offer environment determines where the existing offer details are fetched from.
                      </Typography>
                      <Typography variant="body2">
                        The offer must exist in public offer, otherwise we cannot fetch it.
                      </Typography>
                      <Typography variant="body2">
                        New offer will be created in staging with 2 years of availability at $100/night.
                      </Typography>
                    </Grid>
                    <Grid size={{ xs: 6 }}>
                      <Controller
                        name="offerId"
                        control={control}
                        render={({ field }) => (
                          <TextField {...field} label="Offer ID" fullWidth required sx={{ mb: 2 }} />
                        )}
                      />
                    </Grid>
                    <Grid size={{ xs: 6 }}>
                      <Controller
                        name="offerEnvironment"
                        control={control}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            select
                            label="Offer environment"
                            fullWidth
                            required
                            sx={{ mb: 2 }}
                            helperText="Select the environment to fetch the existing offer details from."
                          >
                            <MenuItem value="production">Production</MenuItem>
                            <MenuItem value="staging">Staging</MenuItem>
                          </TextField>
                        )}
                      />
                    </Grid>
                  </Grid>
                </Box>
              )}

              {selectedType === 'new' && (
                <Box>
                  <Typography variant="h6" sx={{ mb: 2 }}>
                    New Offer Details
                  </Typography>
                  <Grid container spacing={1}>
                    <Grid size={{ xs: 12 }} sx={{ mb: 2 }}>
                      <Typography variant="body2">
                        Fill in the following form to make an offer in the staging environment.
                      </Typography>
                      <Typography variant="body2">
                        New offer will be created in staging with 2 years of availability at $100/night.
                      </Typography>
                    </Grid>

                    <Grid size={{ xs: 12, md: 6 }}>
                      <Controller
                        name="name"
                        control={control}
                        render={({ field }) => (
                          <TextField {...field} label="Offer Name" fullWidth required sx={{ mb: 2 }} />
                        )}
                      />
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                      <Controller
                        name="offerType"
                        control={control}
                        render={({ field }) => (
                          <TextField {...field} select label="Offer Type" fullWidth required sx={{ mb: 2 }}>
                            <MenuItem value="hotel">Limited Time Lux Exclusive</MenuItem>
                            <MenuItem value="tactical_ao_hotel">Lux Premium Collection</MenuItem>
                          </TextField>
                        )}
                      />
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                      <Controller
                        name="city"
                        control={control}
                        render={({ field }) => <TextField {...field} label="City" fullWidth required sx={{ mb: 2 }} />}
                      />
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                      <Controller
                        name="country"
                        control={control}
                        render={({ field }) => (
                          <TextField {...field} label="Country" fullWidth required sx={{ mb: 2 }} />
                        )}
                      />
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                      <Controller
                        name="vendorId"
                        control={control}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            label="Vendor ID"
                            fullWidth
                            sx={{ mb: 2 }}
                            helperText="Amend if different vendor required"
                          />
                        )}
                      />
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                      <Controller
                        name="vendorEmail"
                        control={control}
                        render={({ field }) => <TextField {...field} label="Vendor Email" fullWidth sx={{ mb: 2 }} />}
                      />
                    </Grid>
                  </Grid>
                </Box>
              )}

              {selectedType === 'import' && (
                <Box>
                  <Typography variant="h6" sx={{ mb: 2 }}>
                    Import Production Offer
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid size={{ xs: 12 }} sx={{ mb: 1 }}>
                      <Typography variant="body2">Import an offer from production into staging.</Typography>
                      <Typography variant="body2">
                        This will copy the offer, property, and vendor data from production to staging.
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        Note: The offer, property, and vendor IDs will be the same as in production.
                      </Typography>
                      <Typography variant="body2">Only self-managed flash offers can be imported.</Typography>
                    </Grid>
                    <Grid size={{ xs: 12 }}>
                      <Controller
                        name="offerId"
                        control={control}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            label="Production Offer ID"
                            fullWidth
                            required
                            sx={{ mb: 2 }}
                            helperText="Enter the Salesforce ID of the production offer to import"
                          />
                        )}
                      />
                    </Grid>
                  </Grid>
                </Box>
              )}

              <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                <Button onClick={handleCancel}>Cancel</Button>
                <Button variant="contained" type="submit" disabled={loading}>
                  {loading ? 'Creating...' : 'Create Offer'}
                </Button>
              </Box>
            </Box>
          )}

          {currentStep === MAKE_ME_AN_OFFER_SETUP_STEPS.PROCESSING && (
            <Box sx={{ minHeight: '400px', display: 'flex', flexDirection: 'column' }}>
              <Typography variant="h6" sx={{ mb: 3 }}>
                {loading ? 'Creating Your Offer...' : offerUrl ? 'Success!' : 'Process Failed'}
              </Typography>

              <Box sx={{ flex: 1 }}>
                <MakeMeAnOfferCreateStatus
                  name={createdOfferName}
                  status={makeMeAnOfferStatus}
                  offerUrl={offerUrl}
                  errorMessage={errorMessage}
                  flowType={selectedType}
                />
              </Box>

              {!loading && (
                <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                  {offerUrl && (
                    <Button variant="contained" color="primary" href={offerUrl} target="_blank">
                      View Offer
                    </Button>
                  )}
                  <Button onClick={onModalClose}>Close</Button>
                </Box>
              )}
            </Box>
          )}
        </form>
      </Box>
    </Modal>
  );
}
